/**
 * PaymentPortal.jsx — AOS-PAY-UNIFIED-01 Student-Facing Payment Workspace
 *
 * Routes:
 *   /campus/pay                  — Main payment portal (gateway checkout)
 *   /campus/pay/upi/:intentId    — UPI QR display
 *   /campus/pay/status/:intentId — Payment status / receipt
 *   /campus/pay/history          — Payment history
 *
 * Simulator Mode:
 *   When gateway_mode === 'SIMULATOR', a clearly labeled orange banner appears.
 *   Simulated payments are never presented as real gateway transactions. (INV-PAY-SEC-05)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  CreditCard,
  Smartphone,
  QrCode,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Download,
  AlertTriangle,
  ChevronRight,
  ArrowLeft,
  Receipt,
  Wifi,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ─────────────────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem("academiaos_token");
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

function fmtAmount(amount, currency = "INR") {
  const n = parseFloat(amount);
  if (isNaN(n)) return "₹—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    minimumFractionDigits: 2,
  }).format(n);
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── Simulator Banner ─────────────────────────────────────────────────────────

function SimulatorBanner({ mode }) {
  if (mode !== "SIMULATOR") return null;
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-amber-500 bg-amber-50 px-4 py-2.5 text-amber-800 text-sm font-medium"
      data-testid="simulator-banner"
    >
      <AlertTriangle size={15} className="shrink-0 text-amber-600" />
      <span>
        <strong>SIMULATOR MODE</strong> — No real payment will be processed.
        Razorpay sandbox credentials not configured.
      </span>
    </div>
  );
}

// ── Channel Selection ─────────────────────────────────────────────────────────

const CHANNELS = [
  {
    id: "gateway",
    label: "Card / Net Banking",
    sublabel: "Pay via Razorpay Checkout",
    icon: CreditCard,
    color: "blue",
  },
  {
    id: "upi_qr",
    label: "UPI QR Code",
    sublabel: "Scan & pay with any UPI app",
    icon: QrCode,
    color: "violet",
  },
  {
    id: "upi_intent",
    label: "UPI App Intent",
    sublabel: "Open your UPI app directly",
    icon: Smartphone,
    color: "green",
  },
];

function ChannelCard({ channel, selected, onSelect }) {
  const Icon = channel.icon;
  const colors = {
    blue: "border-blue-500 bg-blue-50",
    violet: "border-violet-500 bg-violet-50",
    green: "border-emerald-500 bg-emerald-50",
  };
  const iconColors = {
    blue: "text-blue-600",
    violet: "text-violet-600",
    green: "text-emerald-600",
  };

  return (
    <button
      onClick={() => onSelect(channel.id)}
      data-testid={`channel-${channel.id}`}
      className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all hover:shadow-md w-full
        ${selected ? colors[channel.color] : "border-slate-200 bg-white hover:border-slate-300"}`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg
          ${selected ? `bg-white shadow-sm ${iconColors[channel.color]}` : "bg-slate-100 text-slate-500"}`}
      >
        <Icon size={20} />
      </span>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm ${selected ? "text-slate-900" : "text-slate-700"}`}>
          {channel.label}
        </p>
        <p className="text-xs text-slate-500 truncate">{channel.sublabel}</p>
      </div>
      {selected && <CheckCircle size={16} className={iconColors[channel.color]} />}
    </button>
  );
}

// ── UPI QR Display ────────────────────────────────────────────────────────────

function UpiQrPanel({ intentId, amount, currency, gatewayMode, onSuccess }) {
  const [qr, setQr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  const createQr = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/pay/upi/qr/create`,
        { intent_id: intentId, transaction_note: "AcademiaOS Fee Payment" },
        { headers: authHeaders() }
      );
      setQr(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create UPI QR");
    } finally {
      setLoading(false);
    }
  }, [intentId]);

  useEffect(() => {
    createQr();
  }, [createQr]);

  // Countdown timer
  useEffect(() => {
    if (!qr?.expires_at) return;
    const tick = () => {
      const diff = Math.max(0, new Date(qr.expires_at) - Date.now());
      setTimeLeft(Math.floor(diff / 1000));
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [qr?.expires_at]);

  // Poll status
  useEffect(() => {
    if (!qr?.qr_id || qr.status !== "ACTIVE") return;
    const iv = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/api/pay/upi/qr/${qr.qr_id}`, {
          headers: authHeaders(),
        });
        if (r.data.status === "PAID") {
          setQr((p) => ({ ...p, status: "PAID" }));
          clearInterval(iv);
          onSuccess?.(r.data.fm3_payment_id);
        } else if (r.data.status === "EXPIRED") {
          setQr((p) => ({ ...p, status: "EXPIRED" }));
          clearInterval(iv);
        }
      } catch (_) {}
    }, 3000);
    return () => clearInterval(iv);
  }, [qr?.qr_id, qr?.status, onSuccess]);

  const simulatePaid = async () => {
    if (!qr?.qr_id) return;
    setSimulating(true);
    try {
      const r = await axios.post(
        `${API}/api/pay/upi/qr/${qr.qr_id}/simulate-paid`,
        {},
        { headers: authHeaders() }
      );
      toast.success("[SIMULATOR] UPI payment simulated");
      onSuccess?.(r.data.fm3_payment_id);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <RefreshCw size={24} className="animate-spin text-violet-500" />
        <p className="text-sm text-slate-500">Generating UPI QR…</p>
      </div>
    );
  }

  if (!qr) return null;

  const mins = timeLeft !== null ? Math.floor(timeLeft / 60) : null;
  const secs = timeLeft !== null ? timeLeft % 60 : null;

  return (
    <div className="flex flex-col items-center gap-4" data-testid="upi-qr-panel">
      {qr.status === "PAID" ? (
        <div className="flex flex-col items-center gap-2 text-center">
          <CheckCircle size={48} className="text-emerald-500" />
          <p className="font-semibold text-emerald-700">UPI Payment Received</p>
        </div>
      ) : qr.status === "EXPIRED" ? (
        <div className="flex flex-col items-center gap-2 text-center">
          <XCircle size={48} className="text-red-400" />
          <p className="font-semibold text-red-600">QR Expired</p>
          <button
            onClick={createQr}
            className="mt-2 rounded-lg bg-violet-600 px-4 py-2 text-sm text-white hover:bg-violet-700"
          >
            Generate New QR
          </button>
        </div>
      ) : (
        <>
          {/* QR Code */}
          <div className="relative rounded-2xl border-4 border-violet-200 bg-white p-4 shadow-inner">
            {qr.is_simulated && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-amber-500 px-3 py-0.5 text-xs font-bold text-white whitespace-nowrap">
                SIMULATOR — NOT PAYABLE
              </div>
            )}
            <QRCodeSVG
              value={qr.upi_deep_link}
              size={200}
              level="M"
              includeMargin={false}
            />
          </div>

          {/* Amount */}
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">
              {fmtAmount(amount, currency)}
            </p>
            {qr.vpa && (
              <p className="text-xs text-slate-500 mt-0.5">Pay to: {qr.vpa}</p>
            )}
          </div>

          {/* Countdown */}
          {timeLeft !== null && timeLeft > 0 && (
            <div className="flex items-center gap-1.5 text-sm text-slate-500">
              <Clock size={14} />
              <span>
                Expires in {mins}:{String(secs).padStart(2, "0")}
              </span>
            </div>
          )}

          {/* Polling indicator */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Wifi size={12} className="animate-pulse" />
            Waiting for payment confirmation…
          </div>

          {/* Simulator pay button */}
          {qr.is_simulated && (
            <button
              onClick={simulatePaid}
              disabled={simulating}
              data-testid="btn-simulate-upi-paid"
              className="flex items-center gap-2 rounded-lg border border-amber-400 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            >
              {simulating ? (
                <RefreshCw size={14} className="animate-spin" />
              ) : (
                <AlertTriangle size={14} />
              )}
              [SIMULATOR] Simulate UPI Payment
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ── Gateway Checkout Panel ────────────────────────────────────────────────────

function GatewayCheckoutPanel({ intentId, amount, currency, gatewayMode, onSuccess }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [simulating, setSimulating] = useState(false);

  const createOrder = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/pay/razorpay/order`,
        { intent_id: intentId },
        { headers: authHeaders() }
      );
      setOrder(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to create order");
    } finally {
      setLoading(false);
    }
  }, [intentId]);

  useEffect(() => {
    createOrder();
  }, [createOrder]);

  const simulateSuccess = async () => {
    setSimulating(true);
    try {
      const r = await axios.post(
        `${API}/api/pay/razorpay/simulate`,
        { intent_id: intentId, simulate_fail: false },
        { headers: authHeaders() }
      );
      toast.success("[SIMULATOR] Payment simulated successfully");
      onSuccess?.(r.data.fm3_payment_id);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  const simulateFail = async () => {
    setSimulating(true);
    try {
      await axios.post(
        `${API}/api/pay/razorpay/simulate`,
        { intent_id: intentId, simulate_fail: true },
        { headers: authHeaders() }
      );
      toast.info("[SIMULATOR] Payment failure simulated");
    } catch (e) {
      toast.error("Simulation failed");
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center gap-3 py-8">
        <RefreshCw size={24} className="animate-spin text-blue-500" />
        <p className="text-sm text-slate-500">Creating payment order…</p>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="flex flex-col gap-4" data-testid="gateway-checkout-panel">
      {/* Order summary */}
      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Amount</span>
          <span className="font-semibold">{fmtAmount(amount, currency)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Order ID</span>
          <span className="font-mono text-xs text-slate-600 truncate max-w-[200px]">
            {order.razorpay_order_id}
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-slate-500">Gateway</span>
          <span
            className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
              order.gateway_mode === "SIMULATOR"
                ? "bg-amber-100 text-amber-700"
                : "bg-emerald-100 text-emerald-700"
            }`}
          >
            {order.gateway_mode}
          </span>
        </div>
      </div>

      {order.gateway_mode === "SIMULATOR" ? (
        <div className="flex flex-col gap-2">
          <button
            onClick={simulateSuccess}
            disabled={simulating}
            data-testid="btn-simulate-gateway-success"
            className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {simulating ? (
              <RefreshCw size={16} className="animate-spin" />
            ) : (
              <CreditCard size={16} />
            )}
            [SIMULATOR] Pay Now — Simulate Success
          </button>
          <button
            onClick={simulateFail}
            disabled={simulating}
            data-testid="btn-simulate-gateway-fail"
            className="flex items-center justify-center gap-2 rounded-xl border border-red-300 bg-red-50 py-2.5 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
          >
            <XCircle size={14} />
            [SIMULATOR] Simulate Payment Failure
          </button>
          <p className="text-center text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-200">
            Razorpay Checkout unavailable — no credentials configured.
            Real checkout loads when RAZORPAY_KEY_ID is set.
          </p>
        </div>
      ) : (
        <button
          onClick={() => {
            // Production: open Razorpay Checkout
            const options = {
              key: order.key_id,
              amount: order.amount_paise,
              currency: order.currency,
              order_id: order.razorpay_order_id,
              name: "AcademiaOS",
              description: "Fee Payment",
              handler: async (response) => {
                try {
                  const r = await axios.post(
                    `${API}/api/pay/razorpay/verify`,
                    {
                      intent_id: intentId,
                      razorpay_order_id: response.razorpay_order_id,
                      razorpay_payment_id: response.razorpay_payment_id,
                      razorpay_signature: response.razorpay_signature,
                    },
                    { headers: authHeaders() }
                  );
                  toast.success("Payment verified successfully");
                  onSuccess?.(r.data.fm3_payment_id);
                } catch (e) {
                  toast.error("Payment verification failed");
                }
              },
              theme: { color: "#2563EB" },
            };
            const rzp = new window.Razorpay(options);
            rzp.open();
          }}
          data-testid="btn-razorpay-checkout"
          className="flex items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
        >
          <CreditCard size={16} />
          Pay {fmtAmount(amount, currency)} with Razorpay
        </button>
      )}
    </div>
  );
}

// ── Status Pill ───────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  const cfg = {
    SUCCEEDED: { label: "Paid", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
    PAID: { label: "Paid", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
    PENDING: { label: "Pending", cls: "bg-amber-100 text-amber-700", icon: Clock },
    CREATED: { label: "Initiated", cls: "bg-blue-100 text-blue-700", icon: RefreshCw },
    FAILED: { label: "Failed", cls: "bg-red-100 text-red-700", icon: XCircle },
    CANCELLED: { label: "Cancelled", cls: "bg-slate-100 text-slate-600", icon: XCircle },
    EXPIRED: { label: "Expired", cls: "bg-slate-100 text-slate-500", icon: Clock },
  };
  const c = cfg[status] || cfg.PENDING;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.cls}`}>
      <Icon size={11} />
      {c.label}
    </span>
  );
}

// ── Main PaymentPortal Page ───────────────────────────────────────────────────

export default function PaymentPortal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const intentId = searchParams.get("intent_id");
  const bookingId = searchParams.get("booking_id");
  const applicationId = searchParams.get("application_id");
  const amount = searchParams.get("amount");
  const currency = searchParams.get("currency") || "INR";
  const contextLabel = searchParams.get("context_label") || "Fee Payment";

  const [intent, setIntent] = useState(null);
  const [activeChannel, setActiveChannel] = useState("gateway");
  const [loadingIntent, setLoadingIntent] = useState(!!intentId); // start true if we have intentId
  const [gatewayMode, setGatewayMode] = useState("SIMULATOR");

  // Load or create payment intent
  useEffect(() => {
    if (!intentId) return;
    setLoadingIntent(true);
    axios
      .get(`${API}/api/pay/intent/${intentId}`, { headers: authHeaders() })
      .then((r) => {
        setIntent(r.data);
        setGatewayMode(r.data.gateway_mode || "SIMULATOR");
      })
      .catch(() => toast.error("Failed to load payment intent"))
      .finally(() => setLoadingIntent(false));
  }, [intentId]);

  const handlePaymentSuccess = (fm3PaymentId) => {
    navigate(
      `/campus/pay/status/${intentId}?fm3_payment_id=${fm3PaymentId || ""}&context_label=${encodeURIComponent(contextLabel)}`
    );
  };

  if (!intentId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-3">
          <CreditCard size={40} className="mx-auto text-slate-300" />
          <h1 className="text-xl font-bold text-slate-700">Payment Portal</h1>
          <p className="text-sm text-slate-500">
            No payment intent specified. Navigate here from a Hostel Booking or
            Admissions Application.
          </p>
        </div>
      </div>
    );
  }

  if (loadingIntent) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw size={28} className="animate-spin text-blue-500" />
      </div>
    );
  }

  if (intent?.status === "SUCCEEDED") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-sm w-full bg-white rounded-2xl shadow-lg p-8 text-center space-y-4">
          <CheckCircle size={56} className="mx-auto text-emerald-500" />
          <h2 className="text-xl font-bold text-slate-900">Already Paid</h2>
          <p className="text-sm text-slate-500">
            This payment intent has already been fulfilled.
          </p>
          {intent.receipt_number && (
            <p className="rounded-lg bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700">
              Receipt: {intent.receipt_number}
            </p>
          )}
          <button
            onClick={() => navigate(-1)}
            className="w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 p-4 sm:p-6"
      data-testid="payment-portal"
    >
      <div className="mx-auto max-w-md space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">{contextLabel}</h1>
            <p className="text-xs text-slate-500">Choose your payment method</p>
          </div>
        </div>

        <SimulatorBanner mode={gatewayMode} />

        {/* Amount card */}
        <div className="rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 text-white shadow-lg">
          <p className="text-sm opacity-80 mb-1">Amount Due</p>
          <p className="text-3xl font-black" data-testid="payment-amount">
            {fmtAmount(intent?.amount || amount, currency)}
          </p>
          {intent?.receipt_number && (
            <p className="text-xs opacity-75 mt-1">
              Receipt: {intent.receipt_number}
            </p>
          )}
        </div>

        {/* Channel picker */}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-700">Payment Method</p>
          <div className="grid gap-2">
            {CHANNELS.map((ch) => (
              <ChannelCard
                key={ch.id}
                channel={ch}
                selected={activeChannel === ch.id}
                onSelect={setActiveChannel}
              />
            ))}
          </div>
        </div>

        {/* Channel panel */}
        <div className="rounded-2xl bg-white border border-slate-200 p-5 shadow-sm">
          {activeChannel === "gateway" && (
            <GatewayCheckoutPanel
              intentId={intentId}
              amount={intent?.amount || amount}
              currency={currency}
              gatewayMode={gatewayMode}
              onSuccess={handlePaymentSuccess}
            />
          )}

          {activeChannel === "upi_qr" && (
            <UpiQrPanel
              intentId={intentId}
              amount={intent?.amount || amount}
              currency={currency}
              gatewayMode={gatewayMode}
              onSuccess={handlePaymentSuccess}
            />
          )}

          {activeChannel === "upi_intent" && (
            <div className="flex flex-col items-center gap-4 py-4" data-testid="upi-intent-panel">
              <Smartphone size={40} className="text-emerald-500" />
              <div className="text-center">
                <p className="font-semibold text-slate-800">Open UPI App</p>
                <p className="text-sm text-slate-500 mt-1">
                  Use gateway or QR for a supported flow.
                  Mobile UPI intent requires provider-approved deep-link.
                </p>
              </div>
              <div className="w-full rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-xs text-amber-700 text-center">
                [SIMULATOR] UPI intent deep-link requires Razorpay merchant
                approval. Use Gateway or QR channel in simulator mode.
              </div>
              <button
                onClick={() => setActiveChannel("upi_qr")}
                className="flex items-center gap-2 text-sm font-medium text-violet-600 hover:text-violet-700"
              >
                Use UPI QR instead
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Intent metadata */}
        {intent && (
          <div className="rounded-xl bg-white border border-slate-100 px-4 py-3 space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Transaction Details
            </p>
            <div className="flex justify-between text-xs text-slate-600">
              <span>Intent ID</span>
              <span className="font-mono text-slate-500">{intentId?.slice(0, 16)}…</span>
            </div>
            <div className="flex justify-between text-xs text-slate-600">
              <span>Created</span>
              <span>{fmtDateTime(intent.created_at)}</span>
            </div>
            <div className="flex justify-between text-xs text-slate-600">
              <span>Status</span>
              <StatusPill status={intent.status} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── PaymentHistory (exported separately) ─────────────────────────────────────

export function PaymentHistory() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/pay/history?limit=50`, {
        headers: authHeaders(),
      });
      setPayments(r.data.payments || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div
      className="min-h-screen bg-slate-50 p-4 sm:p-6"
      data-testid="payment-history-page"
    >
      <div className="mx-auto max-w-2xl space-y-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Payment History</h1>
            <p className="text-xs text-slate-500">{total} record{total !== 1 ? "s" : ""} found</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <RefreshCw size={24} className="animate-spin text-blue-500" />
          </div>
        ) : payments.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 py-12 text-center">
            <Receipt size={36} className="mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500 text-sm">No payment records yet.</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="payment-history-list">
            {payments.map((p) => (
              <div
                key={p.intent_id}
                className="flex items-center justify-between rounded-xl bg-white border border-slate-200 px-4 py-3 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`payment-row-${p.intent_id}`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
                      p.status === "SUCCEEDED"
                        ? "bg-emerald-50"
                        : p.status === "FAILED"
                        ? "bg-red-50"
                        : "bg-amber-50"
                    }`}
                  >
                    {p.status === "SUCCEEDED" ? (
                      <CheckCircle size={18} className="text-emerald-500" />
                    ) : p.status === "FAILED" ? (
                      <XCircle size={18} className="text-red-400" />
                    ) : (
                      <Clock size={18} className="text-amber-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {p.payment_context?.replace("_", " ") || "Payment"}
                    </p>
                    <p className="text-xs text-slate-500">{fmtDateTime(p.created_at)}</p>
                    {p.receipt_number && (
                      <p className="text-xs text-emerald-600 font-medium">
                        Receipt: {p.receipt_number}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-900">
                    {fmtAmount(p.amount, p.currency)}
                  </p>
                  <StatusPill status={p.status} />
                  {p.gateway_mode === "SIMULATOR" && (
                    <p className="text-xs text-amber-600 mt-0.5">SIM</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
